import { Disclosure } from "@headlessui/react";
import { CaretDown } from "@phosphor-icons/react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Separator,
  Spinner,
  Text,
} from "@radix-ui/themes";
import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";

import { useWorkflow } from "./context";

import { isNil } from "../../utils/fp";
import { getFileExtension, renderFileIcon } from "../../utils/helpers/files";
import { Markdown } from "../Markdown";

export type ChatAction = {
  label: string;
  onClick(): void;
};

export type ChatMessageProps = {
  /**
   * A unique identifier.
   */
  id: string;
  /**
   * Whether the message appears to be sent by the user, or the AI.
   */
  author: "mine" | "theirs";
  /**
   * The visual presentation of the comment. `"status"` indicates that the
   * message my be continually updated by a streamed response from the API.
   */
  type:
    | "message-simple"
    | "message-confetti"
    | "status"
    | "status-done" // this is probably redundant and can be just a prop passed through
    | "upload";
  /**
   * An optional message to display.
   */
  message: string | undefined;
  /**
   * An array of chat actions that the system can use to offer call to actions
   * to the user. Defaults to an empty array if no actions are provided.
   */
  actions?: ChatAction[] | undefined;
  /** An optional array of files to be rendered as part of the message contents.
   * Typically only part of the thread when the user selects files to upload
   * and we want to reflect the chosen files.
   */
  files?: string[] | undefined;
};

const chatMessageClasses = cx(
  // base styles
  "rounded-xl w-max max-w-[75%]",
  // theirs styles
  "",
  // mine styles
  "data-[author=mine]:self-end data-[author=mine]:border data-[author=mine]:border-puntt-accent-4",
  // theirs + message simple
  "[&[data-author=theirs][data-message-type=message-simple]]:bg-puntt-accent-4",
  // theirs + message confetti
  "[&[data-author=theirs][data-message-type=message-confetti]]:bg-puntt-accent-2",
  // theirs + status
  "[&[data-author=theirs][data-message-type=status]]:bg-puntt-neutral-gray-3",
  "[&[data-author=theirs][data-message-type=status-done]]:bg-puntt-neutral-gray-3",
  // theirs + uploading
  "[&[data-author=theirs][data-message-type=upload]]:bg-puntt-neutral-gray-3",
);

export function ChatMessage(props: ChatMessageProps) {
  const { author, type, message, actions = [], files = [], id } = props;

  const ref = useRef<HTMLDivElement>(null);

  const defaultMessage = () => (
    <Grid gap="2">
      <Text size="3">{message}</Text>

      <Grid
        gap="2"
        data-hidden={!files.length}
        className="data-[hidden=true]:hidden"
      >
        {!isNil(files) &&
          Array.from(files).map((filename) => (
            <Flex key={filename} gap="2" align="start">
              <span className="pt-1">
                {renderFileIcon(getFileExtension(filename))}
              </span>
              <Text className="flex-1 [word-break:break-word]">{filename}</Text>
            </Flex>
          ))}
      </Grid>

      <Flex
        gap="2"
        data-hidden={!actions.length}
        className="data-[hidden=true]:hidden"
      >
        {actions?.map((action, i) => (
          <Button
            key={`.${i}.${action.label}`}
            variant="outline"
            onClick={() => action.onClick()}
          >
            {action.label}
          </Button>
        ))}
      </Flex>
    </Grid>
  );

  const renderCommentContents = useCallback(() => {
    const width = ref.current?.getBoundingClientRect().width;
    const height = ref.current?.getBoundingClientRect().height;

    switch (type) {
      case "status":
      case "status-done":
        return (
          <StatusComment
            message={message}
            done={type === "status-done" ? true : false}
            messageId={id}
          />
        );
      case "upload":
        return <UploadComment />;
      case "message-confetti":
        return (
          <>
            {defaultMessage()}
            <ConfettiExplosion width={width} height={height} duration={5000} />
          </>
        );
      default:
        return defaultMessage();
    }
  }, [defaultMessage, message, type]);

  return (
    <Box
      ref={ref}
      px="4"
      py="2"
      className={chatMessageClasses}
      data-message-type={type}
      data-author={author}
    >
      {renderCommentContents()}
    </Box>
  );
}

function StatusComment({
  message,
  done,
  messageId,
}: {
  message: string | undefined;
  done: boolean;
  messageId: string | undefined;
}) {
  const {
    reviewDocuments,
    incrementActiveCommentIndex,
    assetPreviewIndex,
    reviewTopics,
  } = useWorkflow();

  const [pendingCommentIndex, setPendingCommentIndex] = useState(0);
  const [reviewTopicIndex, setReviewTopicIndex] = useState(0);
  const currentDocument = reviewDocuments.find(
    (doc) => doc.messageId === messageId,
  );

  const pendingComments = currentDocument?.pendingComments;

  useEffect(() => {
    // If there are no pending comments or the document is done, don't set up the interval
    if (
      isNil(pendingComments) ||
      pendingComments.length === 0 ||
      currentDocument?.isDone
    ) {
      return;
    }

    // Set up the interval to increment the index every 2 seconds
    const interval = setInterval(() => {
      setPendingCommentIndex((prevIndex) => {
        if (
          currentDocument?.messageId ===
          reviewDocuments[assetPreviewIndex].messageId
        ) {
          incrementActiveCommentIndex(prevIndex + 1);
        }
        // If we've reached the end of the comments, clear the interval
        if (prevIndex >= pendingComments.length - 1) {
          clearInterval(interval);
          return prevIndex;
        }
        // Otherwise, increment the index
        return prevIndex + 1;
      });
    }, 2000);

    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      clearInterval(interval);
    };
  }, [pendingComments, currentDocument?.isDone]);

  useEffect(() => {
    const interval = setInterval(() => {
      setReviewTopicIndex((prevIndex) => (prevIndex + 1) % reviewTopics.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [reviewTopics]);
  console.log("currentDocument", currentDocument);
  return (
    <Disclosure defaultOpen as={Grid} gap="2">
      <Box>
        <Flex align="center" justify="between">
          <Disclosure.Button>
            <Flex gap="2" align="center">
              <Text>{done ? "Review Completed" : "Working"}</Text>
              <CaretDown className="inline align-text-top" />
            </Flex>
          </Disclosure.Button>
        </Flex>
        <Separator orientation="horizontal" className="w-full" />
      </Box>

      <Disclosure.Panel as={Grid} gap="2">
        <Flex gap="2" align="start">
          <Spinner
            className={cx("mt-[0.2em]", {
              hidden:
                currentDocument?.isDone ||
                !currentDocument?.processStatus.isAIReady,
            })}
          />
          <Box className="[&_p]:line-clamp-3">
            <Markdown>
              {!currentDocument?.isDone
                ? isNil(pendingComments)
                  ? message
                  : (currentDocument?.status ?? "") +
                    "\n" +
                    (pendingComments[pendingCommentIndex]?.comment ??
                      "No issues found yet.")
                : currentDocument.status}
            </Markdown>
          </Box>
        </Flex>

        {!done && !currentDocument?.processStatus.isAIReady && (
          <Flex gap="2" align="center">
            <Spinner />
            <Text className="line-clamp-2">
              {reviewTopics[reviewTopicIndex]}
            </Text>
          </Flex>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
}

function UploadComment() {
  return (
    <Disclosure defaultOpen as={Grid} gap="2">
      <Box>
        <Flex align="center" justify="between">
          <Disclosure.Button>
            <Text>
              Working
              <CaretDown className="inline align-text-top" />
            </Text>
          </Disclosure.Button>
        </Flex>
        <Separator orientation="horizontal" className="w-full" />
      </Box>

      <Disclosure.Panel as={Grid} gap="2">
        <Flex gap="2" align="center">
          <Spinner />
          <Text>Uploading</Text>
        </Flex>
      </Disclosure.Panel>
    </Disclosure>
  );
}
